import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, RHFTextField } from 'components/hook-form';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IconButton, InputAdornment, Stack } from '@mui/material';
import Iconify from 'components/Iconify';
import { LoadingButton } from '@mui/lab';
import axios from 'axios';
import { FORGOT_RESET_PASSWORD_API_URL } from 'api/apiConstants';
import { useSnackbar } from 'notistack';
import { useSearchParams } from 'react-router-dom';
import Invalid from 'components/Invalid';
import LoadingScreen from 'components/LoadingScreen';
import PartialPageSpinner from 'components/PartialPageSpinner';
type Props = {
  onStatus: VoidFunction;
};

function SetPasswordform({ onStatus }: Props) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [tokenStatus, setTokenStatus] = useState(false);
  const [loaderStatus, setLoaderStatus] = useState(true);
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams({
    token: '',
  });
  const { enqueueSnackbar } = useSnackbar();
  type FormValuesProps = {
    new_password: string;
    confirm_password: string;
  };

  const verifyToken = async (token: string | Blob | null | undefined) => {
    if (token != null) {
      try {
        await new Promise(resolve => setTimeout(resolve, 500));
        const tokenFormData = new FormData();
        tokenFormData.append('action', 'verify_token');
        tokenFormData.append('token', token!);
        const respo = await axios.post(
          FORGOT_RESET_PASSWORD_API_URL,
          tokenFormData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
          }
        );
        if (respo.data.success) {
          setTokenStatus(true);
          setLoaderStatus(false);
          enqueueSnackbar(respo.data.message, { variant: 'success' });
        }
      } catch (error) {
        setTokenStatus(false);
        setLoaderStatus(false);
      }
    }
  };

  const token = searchParams.get('token')!;

  useEffect(() => {
    verifyToken(token); // this will fire only on first render
  }, []);

  const ResetPasswordSchema = Yup.object().shape({
    new_password: Yup.string().required(t('password_is_required')),
    confirm_password: Yup.string().oneOf(
      [Yup.ref('new_password'), null],
      t('passwords_must_match')
    ),
  });
  const methods = useForm<FormValuesProps>({
    mode: 'all',
    resolver: yupResolver(ResetPasswordSchema),
    defaultValues: { new_password: '', confirm_password: '' },
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = async (data: FormValuesProps) => {
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      const bodyFormData = new FormData();
      bodyFormData.append('action', 'reset_password');
      bodyFormData.append('new_password', data.new_password);
      bodyFormData.append('token', token!);
      const respo = await axios.post(
        FORGOT_RESET_PASSWORD_API_URL,
        bodyFormData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );
      if (respo.data.success) {
        onStatus();
      } else {
        enqueueSnackbar(respo.data.message, { variant: 'error' });
      }
    } catch (error: any) {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };
  if (loaderStatus) {
    return <PartialPageSpinner />;
  }
  if (tokenStatus && !loaderStatus) {
    return (
      <>
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={3}>
            <RHFTextField
              id='password'
              name='new_password'
              type={showPassword ? 'text' : 'password'}
              autoComplete='current-password'
              label={t('new_password') + '*'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge='end'
                    >
                      <Iconify
                        icon={
                          showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'
                        }
                      />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <RHFTextField
              name='confirm_password'
              label={t('confirm_password') + '*'}
              type={showConfirmPassword ? 'text' : 'password'}
              autoComplete='confirm-new-password'
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton
                      edge='end'
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                    >
                      <Iconify
                        icon={
                          showConfirmPassword
                            ? 'eva:eye-fill'
                            : 'eva:eye-off-fill'
                        }
                      />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <LoadingButton
              fullWidth
              size='large'
              name='submit'
              type='submit'
              variant='contained'
              loading={isSubmitting}
            >
              {t('save_password')}
            </LoadingButton>
          </Stack>
        </FormProvider>
      </>
    );
  }

  if (!tokenStatus && !loaderStatus) {
    return (
      <Invalid
        invalidMessage={t('invalid_token_verification')}
        detailedMessage={t('invalid_token_verification')}
      ></Invalid>
    );
  } else {
    return <LoadingScreen />;
  }
}

export default SetPasswordform;
